// See https://github.com/blackboard-foundations/bb-tenant/blob/master/openapi/Tenant.v1.yaml

export const TenantTypes = [
  'Customer',
  'Learn',
  'Student',
  'Reach',
  'Raise',
  'ReleaseStage',
  'LearnDms',
  'Illuminate',
  'Fleet',
  'StudentSuccess',
  'AFHP',
];

export interface TenantData {
  id: string;
  name: string;
  description?: string;
  uriComponent?: string;
  clientId?: string;
  isHidden?: boolean;
  hostname?: string;
  parentTenant?: string;
  tenantType?: string;
  region?: string;
  displayName?: string;
  fleetId?: string;
  tenantChildren?: string[];
}

export interface TenantSearchableResponse {
  results: TenantData[];
}

export interface DdaClientData {
  id: string;
  name: string;
  description: string;
  tenantId: string;
  tenantType?: string;
  hostName: string;
  ddaCname: string;
  region: string;
  isActive: boolean;
}

export interface DdaUserData {
  id: string;
  tenantId: string;
  userName: string;
  secretName: string;
  isShowSecret?: boolean;
}

export interface DdaIpsData {
  id: string;
  tenantId: string;
  ipAddress: string;
}

export interface DdaSecret {
  secret: string;
  id: string;
  userName: string;
  tenantId?: string;
}

/**
 * Possible types of Auth0 connections:
 * - 'LearnConnector' equates to Foundations Only in the UI.
 * - 'SAML' for SAML connections
 * - 'AzureAD' for Azure AD connections
 */
export type IdpConnectionType = 'LearnConnector' | 'SAML' | 'AzureAD';
export interface IdentityProvidersData {
  id: string;
  name: string;
  type: IdpConnectionType;
  displayName: string;
  description: string;
  clonedFromTenantId?: string;
  clonedFromIdpId?: string;
}

export interface ClientContact {
  email?: string;
  phone?: string;
}
export interface ClientData {
  id: string;
  name: string;
  primaryMarket: string;
  secondaryMarket: string;
  city: string;
  state: string;
  country: string;
  externalIds: { [key: string]: string };
  tenantId: string;
  contact: ClientContact[];
  team: object[];
}

export interface LearnConfigDetails {
  id: string;
  tenantId: string;
  domain: string;
  externalId: string;
  description: string;
  configuration: Record<string, any>;
}

// TODO: Remove these definitions (IJobPreview, IInputPaths, IJobSummary) once the actual types are defined in the job-service package
export interface IJobPreview {
  title: string;
  contentPath?: string;
  chipText?: string;
  chipLinkPath?: string;
  chipTenantIdLinkPath?: string;
}
export interface IInputPaths {
  title: string;
  subtitle?: string;
  tenant?: string;
  link?: string;
}

export interface IJobSummary {
  widgetId: string;
  inputPaths: IInputPaths;
}

export interface FleetData {
  stage: string;
  crowdStrikeLicenseKey: string;
  accountId: string;
  isIL4: boolean;
  isSharedRds: boolean;
  region: string;
  id: string;
  name: string;
  fleetId: string;
  scoreRecommendationsURL: string;
  institutionPagesURL: string;
}

export interface FleetDialogData {
  name: string;
  stage: string;
  fleetId: string;
  region: string;
  accountId: string;
}

export type ArtifactType = 'learn-installer' | 'ultra-ui' | 'ultra-router';
export type ArtifactApprovalType = 'yes' | 'no';
export type ArtifactStageType = 'rel' | 'ci';

export interface IArtifactData {
  artifact_key: string;
  s3_version: string;
  approval: ArtifactApprovalType | null;
  date_created: string;
  artifact_basename: string | null;
  date_approved: string | null;
  approver: string | null;
  checksum_md5: string;
  checksum_sha1: string;
  checksum_sha256: string;
}

export interface IArtifactDataResp {
  items: IArtifactData[];
  next_token?: null | string;
}

export interface CohortNotifications {
  end_suggested_date?: string;
  is_accepted?: boolean;
  start_suggested_date?: string;
  migration_date?: string;
  resend_number?: number;
  status?: string | null;
}

// Cohort types
export interface SiteInCohort {
  captain_id: string;
  cohort: string;
  learn_id?: string;
  learn_url?: string;
  aws_account?: string;
  account_name?: string;
  fleet_name?: string;
  client_name?: string;
  tenant_id?: string;
  upgrade_job_id?: string;
  upgrade_job_status?: string;
  upgrade_job_status_reason?: string;
  date_created?: string;
  date_updated?: string;
  notifications?: CohortNotifications;
  site_status?: string;
}

export type CohortStatus = string;

export interface Cohort {
  id: string;
  name?: string;
  comments?: string;
  status_reason?: string;
  downtime_date?: string;
  enable_notifications?: boolean;
  created_by_user?: string;
  date_created?: string;
  date_updated?: string;
  sites_count: string;
  fleet_id?: string;
  fleet_account_id?: string;
  fleet_region?: string;
  upgrade_workflow_status?: string;
}

export interface CohortDetail extends Cohort {
  migration_status?: CohortStatus;
  sites: SiteInCohort[];
}

// Notification types
export interface NotificationRequest {
  captain_ids: string[];
  notifications: {
    status: 'SENT' | 'PENDING' | 'FAILED';
  };
}

export interface NotificationResponse {
  message: string;
  executionArn: string;
}

export interface DeleteNotificationRequest {
  captain_ids: string[];
}

export interface DeleteNotificationResponse {
  message: string;
  executionArn: string;
}
export interface HierarchyTenant {
  name: string;
  description: string | null;
  region: string;
  fleetId: string | null;
  parentTenant: string | null;
  uriComponent: string;
  clientId: string | null;
  displayName: string | null;
  hostname: string | null;
  stage: string | null;
  externalId: string | null;
  isHidden: boolean;
  globalUniqueCustomerIdentifier: string | null;
  logoDisplayName: string | null;
  logoLink: string | null;
  privacyPolicyLink: string | null;
  technicalSupportLink: string | null;
  tenantType: string | null;
  tenantChildren: string[] | null;
  id: string;
  usedInReportingInd?: boolean;
  dataSharingInd?: boolean;
}
export interface TenantHierarchyResponse {
  tenants: HierarchyTenant[];
}
